import { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle2, FileImage, Search,
  ArrowDownAZ, Building2, Layers, ShieldCheck,
  ChevronDown, ChevronRight, Star, FileCheck2,
  Home,
} from 'lucide-react';

/**
 * LotsSidebar — V14.51 Sprint 6 P0.
 *
 * Human-readable labels (lot_number > name > short id), denormalized
 * building/floor names rendered as a subtitle, and dynamic
 * sort/filter controls : search by label, group/filter by building,
 * group/filter by floor (level-aware), and per-row geometry checklist.
 *
 * Backend extension : GET /projects/{id}/lots now returns
 *   building_name, floor_name, floor_level   (lots.py V14.51)
 */
const STATUS_COLORS = {
  ready:        'bg-teal-500',
  in_progress:  'bg-amber-500',
  pending:      'bg-gray-500',
  blocked:      'bg-rose-500',
};

/** Format a human-readable label for a lot.
 *  Priority : explicit `label` → `lot_number` → `name` (≠ '') → `#xxxxxx`. */
const lotDisplayLabel = (lot) => {
  if (lot.label) return lot.label;
  if (lot.lot_number) return `Lot ${lot.lot_number}`;
  if (lot.name && lot.name.trim()) return lot.name;
  const tail = (lot.lot_id || '').slice(-6).toUpperCase();
  return `Lot #${tail}`;
};

/** Secondary description e.g. "Bât. A · R+1 · T3 · 60 m²" */
const lotSubtitle = (lot) => {
  const parts = [];
  if (lot.building_name) parts.push(`Bât. ${lot.building_name.replace(/^Bâtiment\s+/i, '')}`);
  if (lot.floor_name) parts.push(lot.floor_name);
  if (lot.typology && lot.typology !== lot.name && lot.typology !== `Lot ${lot.lot_number}`) {
    parts.push(lot.typology);
  }
  if (lot.surface && lot.surface > 0) parts.push(`${Math.round(lot.surface)} m²`);
  return parts.join(' · ');
};

const SORT_OPTIONS = [
  { value: 'name_asc',     label: 'Nom (A→Z)',          icon: ArrowDownAZ },
  { value: 'name_desc',    label: 'Nom (Z→A)',          icon: ArrowDownAZ },
  { value: 'building',     label: 'Par bâtiment',       icon: Building2 },
  { value: 'floor',        label: 'Par étage (bas→haut)', icon: Layers },
  { value: 'floor_desc',   label: 'Par étage (haut→bas)', icon: Layers },
];

const LotsSidebar = ({ lots, selectedLotId, onSelect, loading, maisonMode = false, onSelectAndOpenStructure, projectId, onFloorPatched }) => {
  const [search, setSearch] = useState('');
  // Sprint 26B — In Maison mode the default sort is top-floor-first
  // (Combles before RDC before SS) so the sidebar reads like the
  // architect's stack diagram.
  const [sortBy, setSortBy] = useState(maisonMode ? 'floor_desc' : 'name_asc');
  const [filterBuilding, setFilterBuilding] = useState('');
  const [filterFloor, setFilterFloor] = useState('');
  // V14.62 Sprint 17 — quick toggles per user feedback :
  //   • Uniques only  → hide Similaire lots, surface masters only
  //   • Plan dispo   → only lots with checklist.plan_set === true
  const [onlyUnique, setOnlyUnique] = useState(false);
  const [onlyWithPlan, setOnlyWithPlan] = useState(false);
  // Group headers (when sortBy = building / floor) start COLLAPSED so the
  // sidebar shows ~one screen of buildings on long projects (145+ lots).
  // We track which groups the user has EXPANDED (default = none open).
  // The group containing the currently selected lot is always shown open.
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (key) => setExpandedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Distinct buildings + floors for the filter dropdowns.
  const { buildings, floors } = useMemo(() => {
    const b = new Map();
    const f = new Map();
    (lots || []).forEach((l) => {
      if (l.building_id && l.building_name) b.set(l.building_id, l.building_name);
      if (l.floor_id && l.floor_name) {
        f.set(l.floor_id, {
          name: l.floor_name,
          level: l.floor_level,
          building_id: l.building_id,
          hsp_m: l.floor_hsp_m,  // Sprint 30
        });
      }
    });
    return {
      buildings: Array.from(b.entries())
        .map(([id, name]) => ({ id, name }))
        .sort((a, b2) => (a.name || '').localeCompare(b2.name || '')),
      floors: Array.from(f.entries())
        .map(([id, meta]) => ({ id, ...meta }))
        .sort((a, b2) => (a.level ?? 99) - (b2.level ?? 99) || (a.name || '').localeCompare(b2.name || '')),
    };
  }, [lots]);

  const filteredAndSorted = useMemo(() => {
    let items = (lots || []).slice();
    const q = search.trim().toLowerCase();
    if (q) {
      items = items.filter((l) => {
        const hay = [
          l.lot_id, l.lot_number, l.name, l.label, l.building_name, l.floor_name, l.typology,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
    }
    if (filterBuilding) items = items.filter((l) => l.building_id === filterBuilding);
    if (filterFloor)    items = items.filter((l) => l.floor_id === filterFloor);
    // V14.62 — quick toggles
    if (onlyUnique)    items = items.filter((l) => (l.correspondance || 'Unique') === 'Unique');
    if (onlyWithPlan)  items = items.filter((l) => l.checklist?.plan_set === true);
    switch (sortBy) {
      case 'name_desc':
        items.sort((a, b) => lotDisplayLabel(b).localeCompare(lotDisplayLabel(a)));
        break;
      case 'building':
        items.sort((a, b) =>
          (a.building_name || 'zzz').localeCompare(b.building_name || 'zzz')
          || (a.floor_level ?? 99) - (b.floor_level ?? 99)
          || lotDisplayLabel(a).localeCompare(lotDisplayLabel(b)));
        break;
      case 'floor':
        items.sort((a, b) =>
          (a.floor_level ?? 99) - (b.floor_level ?? 99)
          || (a.floor_name || 'zzz').localeCompare(b.floor_name || 'zzz')
          || lotDisplayLabel(a).localeCompare(lotDisplayLabel(b)));
        break;
      case 'floor_desc':
        // Top floor first — matches the visual order of a vertical
        // architectural stack (Combles on top, basement on bottom).
        items.sort((a, b) =>
          (b.floor_level ?? -99) - (a.floor_level ?? -99)
          || (a.floor_name || 'zzz').localeCompare(b.floor_name || 'zzz')
          || lotDisplayLabel(a).localeCompare(lotDisplayLabel(b)));
        break;
      case 'name_asc':
      default:
        items.sort((a, b) => lotDisplayLabel(a).localeCompare(lotDisplayLabel(b)));
    }
    return items;
  }, [lots, search, sortBy, filterBuilding, filterFloor, onlyUnique, onlyWithPlan]);

  // Optional grouping for 'building' and 'floor' sort modes
  const grouped = useMemo(() => {
    if (sortBy === 'building') {
      const map = new Map();
      filteredAndSorted.forEach((l) => {
        const key = l.building_name || '— Sans bâtiment —';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(l);
      });
      return Array.from(map.entries()).map(([title, items]) => ({ title, items, icon: Building2 }));
    }
    if (sortBy === 'floor' || sortBy === 'floor_desc') {
      const map = new Map();
      filteredAndSorted.forEach((l) => {
        const key = l.floor_name || '— Sans étage —';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(l);
      });
      return Array.from(map.entries()).map(([title, items]) => ({ title, items, icon: Layers }));
    }
    return null;
  }, [filteredAndSorted, sortBy]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-gray-400 text-[11px]">
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        Chargement…
      </div>
    );
  }
  if (!lots || lots.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-4 text-center text-gray-400 text-[11px]">
        <FileImage className="w-5 h-5 mb-2 opacity-50" />
        Aucun lot.
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="lots-sidebar">
      {/* SEARCH + FILTERS */}
      <div className="px-2 py-1.5 space-y-1 border-b" style={{ borderColor: 'var(--c-border)' }}>
        <div className="relative">
          <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrer les lots…"
            className="w-full pl-6 pr-1.5 py-1 text-[11px] bg-black/30 border rounded outline-none text-gray-200 focus:border-amber-400/40"
            style={{ borderColor: 'var(--c-border)' }}
            data-testid="lots-search-input"
          />
        </div>
        <div className="flex items-center gap-1">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 px-1 py-1 text-[10px] bg-black/30 border rounded outline-none text-gray-200"
            style={{ borderColor: 'var(--c-border)' }}
            data-testid="lots-sort-select"
            title="Trier les lots"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{`↕ ${o.label}`}</option>
            ))}
          </select>
          <span className="text-[9.5px] text-gray-500 shrink-0"
                data-testid="lots-count-label">
            {filteredAndSorted.length}/{(lots || []).length}
          </span>
        </div>
        {(buildings.length > 1 || floors.length > 1) && (
          <div className="flex items-center gap-1">
            {buildings.length > 1 && (
              <select
                value={filterBuilding}
                onChange={(e) => { setFilterBuilding(e.target.value); setFilterFloor(''); }}
                className="flex-1 px-1 py-1 text-[10px] bg-black/30 border rounded outline-none text-gray-200"
                style={{ borderColor: 'var(--c-border)' }}
                data-testid="lots-filter-building"
                title="Filtrer par bâtiment"
              >
                <option value="">Tous bâtiments</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            {floors.length > 1 && (
              <select
                value={filterFloor}
                onChange={(e) => setFilterFloor(e.target.value)}
                className="flex-1 px-1 py-1 text-[10px] bg-black/30 border rounded outline-none text-gray-200"
                style={{ borderColor: 'var(--c-border)' }}
                data-testid="lots-filter-floor"
                title="Filtrer par étage"
              >
                <option value="">Tous étages</option>
                {floors
                  .filter((f) => !filterBuilding || f.building_id === filterBuilding)
                  .map((f) => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
              </select>
            )}
          </div>
        )}
        {/* V14.62 — quick boolean filters */}
        <div className="flex items-center gap-1">
          <FilterPill active={onlyUnique} onClick={() => setOnlyUnique((v) => !v)}
                      icon={Star} label="Uniques"
                      title="Masquer les lots Similaires (n'afficher que les Uniques)"
                      testId="lots-filter-unique-toggle" />
          <FilterPill active={onlyWithPlan} onClick={() => setOnlyWithPlan((v) => !v)}
                      icon={FileCheck2} label="Plan dispo"
                      title="N'afficher que les lots ayant un plan source défini"
                      testId="lots-filter-plan-toggle" />
        </div>
      </div>

      {/* Sprint 26B — Maison mini-stack : architectural vertical preview
          of the house's floors (Combles on top, basement at bottom).
          Each tier is clickable and binds to the matching lot. */}
      {maisonMode && floors.length > 0 && (
        <MaisonMiniStack
          floors={floors}
          lots={lots}
          selectedLotId={selectedLotId}
          onSelect={onSelectAndOpenStructure || onSelect}
          projectId={projectId}
          onFloorPatched={onFloorPatched}
        />
      )}

      {/* LIST */}
      <ul className="flex-1 overflow-y-auto py-1" data-testid="lots-sidebar-list">
        {grouped ? (
          grouped.map((g) => {
            const Icon = g.icon;
            const containsSelected = !!selectedLotId
              && g.items.some((l) => l.lot_id === selectedLotId);
            const isOpen = expandedGroups.has(g.title) || containsSelected;
            const Chevron = isOpen ? ChevronDown : ChevronRight;
            return (
              <li key={g.title}>
                <button type="button"
                        onClick={() => toggleGroup(g.title)}
                        className="w-full px-2 py-1 text-[9.5px] font-semibold text-gray-400 sticky top-0 bg-black/60 backdrop-blur-sm border-b inline-flex items-center gap-1 hover:bg-black/80 transition-colors"
                        style={{ borderColor: 'var(--c-border)' }}
                        data-testid={`lots-group-toggle-${g.title}`}
                        title={isOpen ? `Réduire ${g.title}` : `Déplier ${g.title}`}>
                  <Chevron className="w-3 h-3 text-gray-500" />
                  <Icon className="w-3 h-3 text-amber-400" />
                  <span className="truncate">{g.title}</span>
                  <span className="ml-auto text-gray-500 font-normal">{g.items.length}</span>
                </button>
                {isOpen && (
                  <ul>
                    {g.items.map((lot) => (
                      <LotRow key={lot.lot_id} lot={lot}
                              isSelected={lot.lot_id === selectedLotId}
                              onSelect={onSelect} />
                    ))}
                  </ul>
                )}
              </li>
            );
          })
        ) : (
          filteredAndSorted.map((lot) => (
            <LotRow key={lot.lot_id} lot={lot}
                    isSelected={lot.lot_id === selectedLotId}
                    onSelect={onSelect} />
          ))
        )}
        {filteredAndSorted.length === 0 && (
          <li className="px-3 py-4 text-center text-[10.5px] text-gray-500 italic">
            Aucun lot ne correspond à ces filtres.
          </li>
        )}
      </ul>
    </div>
  );
};

const LotRow = ({ lot, isSelected, onSelect }) => {
  const c = lot.checklist || {};
  const title = lotDisplayLabel(lot);
  const sub = lotSubtitle(lot);
  const reviewed = !!c.plan_reviewed;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(lot.lot_id)}
        className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 hover:bg-white/5 transition-colors ${isSelected
          ? 'bg-amber-500/15 border-l-2 border-amber-400'
          : 'border-l-2 border-transparent'}`}
        data-testid={`lot-row-${lot.lot_id}`}
        title={`${title}\n${sub}${reviewed ? '\nPlan revu' : ''}\nID technique : ${lot.lot_id}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_COLORS[lot.status] || STATUS_COLORS.pending}`} />
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1">
            <span className="text-[11.5px] font-medium truncate"
                  style={{ color: isSelected ? 'var(--c-gold)' : 'var(--c-text)' }}>
              {title}
            </span>
            {reviewed && (
              <span
                className="inline-flex items-center gap-0.5 px-1 py-[1px] rounded text-[8.5px] font-semibold tracking-wide uppercase shrink-0 border border-emerald-400/50 text-emerald-200 bg-emerald-500/15"
                data-testid={`lot-plan-reviewed-badge-${lot.lot_id}`}
                title={lot.plan_reviewed_at ? `Plan revu le ${new Date(lot.plan_reviewed_at).toLocaleDateString('fr-FR')}` : 'Plan revu'}
              >
                <ShieldCheck className="w-2.5 h-2.5" />
                Revu
              </span>
            )}
          </span>
          {sub && (
            <span className="block text-[9.5px] text-gray-500 truncate">{sub}</span>
          )}
        </span>
        <span className="flex items-center gap-0.5 shrink-0">
          <Dot ok={c.plan_set}      title="Plan source" />
          <Dot ok={c.polygon_drawn} title="Contour tracé" />
          <Dot ok={c.validated}     title="Géométrie validée" />
          <Dot ok={c.rendered}      title="Rendu 3D" />
        </span>
      </button>
    </li>
  );
};

const Dot = ({ ok, title }) => (
  <span title={title} aria-label={title}
        className={`w-2 h-2 rounded-full inline-flex items-center justify-center ${ok
          ? 'bg-teal-400/80'
          : 'bg-white/10 border border-white/20'}`}>
    {ok ? <CheckCircle2 className="w-2 h-2 text-white/90" /> : null}
  </span>
);

const FilterPill = ({ active, onClick, icon: Icon, label, title, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testId}
    aria-pressed={active}
    className={`inline-flex items-center gap-1 px-1.5 py-[3px] rounded text-[10px] border transition-colors ${
      active
        ? 'bg-amber-500/15 border-amber-400/60 text-amber-200'
        : 'bg-black/30 border-white/10 text-gray-400 hover:text-gray-200 hover:border-white/30'
    }`}
  >
    <Icon className="w-2.5 h-2.5" />
    {label}
  </button>
);

/**
 * MaisonMiniStack — Sprint 26B.
 *
 * Compact architectural preview of the house's floors, displayed at the
 * top of the LotsSidebar when the project is a Maison. Renders one row
 * per floor, top-down (Combles → SS). Each row is clickable and selects
 * the matching lot. The selected floor gets an emerald highlight, the
 * basement(s) get a subdued sky tone so the user sees underground vs
 * above-ground levels at a glance.
 *
 * If a floor has no associated lot (unusual for a freshly-seeded house),
 * the row is rendered disabled so the visual stays consistent.
 */
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASTILLE_TONES = {
  green:  { color: '#34d399' },
  orange: { color: '#fbbf24' },
  red:    { color: '#f87171' },
  grey:   { color: '#6b7280' },
};

const MaisonMiniStack = ({ floors, lots, selectedLotId, onSelect, projectId, onFloorPatched }) => {
  // Ordered from top floor down to basement.
  const ordered = (floors || []).slice().sort(
    (a, b) => (b.level ?? -99) - (a.level ?? -99),
  );
  const lotByFloorId = useMemo(() => {
    const m = new Map();
    (lots || []).forEach((l) => { if (l.floor_id) m.set(l.floor_id, l); });
    return m;
  }, [lots]);

  // Sprint 30 — Éditeur HSP inline (prompt natif — UX minimaliste pour la
  // mini-stack compacte). Le callback `onFloorPatched(floor)` remonte la
  // mise à jour vers le parent qui rafraîchit sa liste `floors`.
  const patchHsp = useCallback(async (floor, e) => {
    e.stopPropagation();
    const current = Number.isFinite(floor.hsp_m) ? floor.hsp_m : 2.50;
    // eslint-disable-next-line no-alert
    const raw = window.prompt(
      `HSP (Hauteur Sous Plafond) pour "${floor.name}" — en mètres, entre 1.80 et 4.00 :`,
      current.toFixed(2),
    );
    if (raw == null) return;
    const val = parseFloat(raw.replace(',', '.'));
    if (!Number.isFinite(val) || val < 1.8 || val > 4.0) {
      toast.error('HSP invalide (attendu entre 1.80 et 4.00 m).');
      return;
    }
    try {
      const { data } = await axios.patch(
        `${API_URL}/floors/${floor.id || floor.floor_id}`,
        { hsp_m: val },
        { withCredentials: true },
      );
      toast.success(`HSP mise à jour : ${data.name} → ${data.hsp_m.toFixed(2)} m`);
      if (onFloorPatched) onFloorPatched(data);
    } catch {
      toast.error('Impossible de sauvegarder la HSP.');
    }
  }, [onFloorPatched]);

  // Sprint 26 improvement — Coherence pastilles. Fetch once per
  // projectId change; cheap O(N floors) backend loop.
  const [coherence, setCoherence] = useState(null);
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/projects/${projectId}/maison/coherence`,
      { withCredentials: true })
      .then((r) => { if (!cancelled) setCoherence(r.data); })
      .catch(() => { if (!cancelled) setCoherence(null); });
    return () => { cancelled = true; };
  }, [projectId]);

  const statusByFloor = useMemo(() => {
    const m = new Map();
    (coherence?.floors || []).forEach((s) => { m.set(s.floor_id, s); });
    return m;
  }, [coherence]);

  return (
    <div className="px-2 py-1.5 border-b" style={{ borderColor: 'var(--c-border)' }}
         data-testid="lots-maison-mini-stack">
      <div className="text-[9px] uppercase tracking-[0.18em] text-emerald-300/70 mb-1 inline-flex items-center gap-1">
        <Home className="w-2.5 h-2.5" />
        Empilement
        <span className="ml-auto text-gray-500 tracking-normal normal-case font-normal">
          {ordered.length} niv.
        </span>
      </div>
      <div className="flex flex-col gap-0.5">
        {ordered.map((f) => {
          const lot = lotByFloorId.get(f.id);
          const selected = lot && lot.lot_id === selectedLotId;
          const underground = (f.level ?? 0) < 0;
          const palette = selected
            ? 'bg-emerald-500/25 border-emerald-400/70 text-emerald-100 shadow-[0_0_0_1px_rgba(16,185,129,0.4)]'
            : underground
              ? 'bg-sky-500/10 border-sky-400/30 text-sky-200 hover:bg-sky-500/15'
              : 'bg-emerald-500/8 border-emerald-400/20 text-emerald-100/80 hover:bg-emerald-500/15';
          const levelLabel = f.level >= 0 ? `+${f.level}` : `${f.level}`;
          const status = statusByFloor.get(f.id);
          const pastille = status ? PASTILLE_TONES[status.status] : null;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => lot && onSelect && onSelect(lot.lot_id)}
              disabled={!lot}
              data-testid={`mini-stack-floor-${f.id}`}
              aria-pressed={selected}
              title={lot ? `${f.name} — sélectionner${status?.status_reason ? ` · ${status.status_reason}` : ''}` : `${f.name} — aucun lot associé`}
              className={`group flex items-center gap-2 px-2 py-1 rounded border text-[10.5px] transition-all disabled:opacity-30 disabled:cursor-not-allowed ${palette}`}
            >
              {pastille && (
                <span className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: pastille.color,
                               boxShadow: `0 0 6px ${pastille.color}66` }}
                      data-testid={`mini-stack-pastille-${f.id}`}
                      data-status={status.status} />
              )}
              <span className="font-mono text-[9px] w-7 text-center opacity-70">
                {levelLabel}
              </span>
              <span className="flex-1 text-left truncate font-medium">
                {f.name}
              </span>
              {/* Sprint 30 — Badge HSP cliquable pour édition inline. */}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => patchHsp(f, e)}
                onKeyDown={(e) => { if (e.key === 'Enter') patchHsp(f, e); }}
                title={`HSP = ${Number.isFinite(f.hsp_m) ? f.hsp_m.toFixed(2) : '2.50'} m — clic pour éditer`}
                data-testid={`mini-stack-hsp-${f.id}`}
                className="font-mono text-[9px] px-1 rounded bg-black/25 border border-white/10 text-amber-200/90 hover:border-amber-400/40 hover:text-amber-200 shrink-0 cursor-pointer"
              >
                {(Number.isFinite(f.hsp_m) ? f.hsp_m : 2.50).toFixed(2)}m
              </span>
              {lot?.checklist?.plan_set && (
                <span title="Plan source défini"
                      className="inline-block w-1.5 h-1.5 rounded-full bg-teal-400/80 shrink-0" />
              )}
              {lot?.checklist?.validated && (
                <ShieldCheck className="w-2.5 h-2.5 text-emerald-300 shrink-0" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LotsSidebar;
